import { rpcClient } from '@/api/rpc/client';
import type { FeedbackSupplier, MassRenewalRun } from '@erp/rpc';
import {
  stagedAt,
  stageWorkbooks,
  type UploadStartPhase,
} from './stageWorkbooks';

export interface MassRenewalDrop {
  /** Whose Results Workbook it is. */
  supplier: FeedbackSupplier;
  resultsWorkbook: File;
  /** One to nine filled-in Feedback Files. */
  feedbackFiles: File[];
  exceptionList?: File | null;
  exceptionLists?: File[];
}

/**
 * Stage the drop in blob storage and start the Mass Renewal run.
 *
 * Every workbook goes straight to storage through a presigned URL (see
 * `stageWorkbooks`), in the order Results Workbook, Feedback Files, Exception
 * List, and `start` is given the staged keys with the original names. Nothing
 * binary passes through the API server.
 */
export async function startMassRenewalRun(
  drop: MassRenewalDrop,
  onPhase: (phase: UploadStartPhase) => void,
): Promise<MassRenewalRun> {
  const exceptionFiles = [
    ...(drop.exceptionLists ?? []),
    ...(drop.exceptionList ? [drop.exceptionList] : []),
  ];
  const staged = await stageWorkbooks(
    [drop.resultsWorkbook, ...drop.feedbackFiles, ...exceptionFiles],
    rpcClient.pricing.massRenewal.getUploadUrl,
    onPhase,
  );
  const feedbackEnd = 1 + drop.feedbackFiles.length;
  onPhase({ phase: 'starting' });
  const run = {
    supplier: drop.supplier,
    resultsWorkbook: stagedAt(staged, 0),
    feedbackFiles: staged.slice(1, feedbackEnd),
  };
  // Any Exception List needs the versioned entry point, so an older API or
  // worker refuses it instead of dropping it; a run without one keeps `start`.
  return exceptionFiles.length
    ? rpcClient.pricing.massRenewal.startWithExceptionLists({
        ...run,
        exceptionLists: staged.slice(feedbackEnd),
      })
    : rpcClient.pricing.massRenewal.start(run);
}
